import { Table } from '@/components/ui/Table'
import { UserIdentity } from '@/components/ui/UserIdentity'
import { ROUTES, buildRoute } from '@/constants/routes'
import { Badge, Text } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const columnHelper = createColumnHelper()

/**
 * UsersTable Component
 * Displays users in a table with avatars, roles, and status
 */
export function UsersTable({
  data = [],
  isLoading,
  pageCount,
  totalCount,
  pagination,
  sorting,
  onPaginationChange,
  onSortingChange,
}) {
  const navigate = useNavigate()
  const [rowSelection, setRowSelection] = useState({})

  const columns = [
    // Checkbox column
    columnHelper.display({
      id: 'select',
      header: ({ table }) => {
        const allSelected = table.getIsAllRowsSelected()
        const someSelected = table.getIsSomeRowsSelected()
        return (
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected && !allSelected
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
            style={{
              cursor: 'pointer',
              width: '16px',
              height: '16px',
              accentColor: '#0052CC',
              borderRadius: '3px',
            }}
          />
        )
      },
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
          style={{
            cursor: row.getCanSelect() ? 'pointer' : 'not-allowed',
            width: '16px',
            height: '16px',
            accentColor: '#0052CC',
            borderRadius: '3px',
            opacity: row.getCanSelect() ? 1 : 0.5,
          }}
        />
      ),
      meta: {
        width: '48px',
      },
    }),
    columnHelper.accessor('id', {
      header: 'ID',
      cell: (info) => {
        const userId = info.getValue()
        return (
          <Text
            color="#0052CC"
            fontSize="14px"
            fontWeight="500"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={() => navigate(buildRoute(ROUTES.USERS_DETAIL, { id: userId }))}
          >
            #{userId}
          </Text>
        )
      },
      meta: {
        width: '80px',
      },
    }),
    columnHelper.accessor((row) => row, {
      id: 'username',
      header: 'User',
      cell: (info) => {
        const user = info.getValue()
        return <UserIdentity user={user} fallbackName={user.username} />
      },
      meta: {
        width: '280px',
      },
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => (
        <Text fontSize="14px" color="#172B4D">
          {info.getValue() || '—'}
        </Text>
      ),
      enableSorting: false,
      meta: {
        width: '220px',
      },
    }),
    columnHelper.accessor((row) => row, {
      id: 'roles',
      header: 'Roles',
      enableSorting: false,
      cell: (info) => {
        const user = info.getValue()
        const isSuperuser = user.is_superuser === true
        const isStaff = user.is_staff === true

        // Determine role badge (only one badge)
        let roleConfig
        if (isSuperuser) {
          roleConfig = { label: 'SUPERADMIN', bg: '#DE350B' }
        } else if (isStaff) {
          roleConfig = { label: 'STAFF', bg: '#0052CC' }
        } else {
          roleConfig = { label: 'USER', bg: '#5E6C84' }
        }

        return (
          <Badge
            bg={roleConfig.bg}
            color="white"
            fontSize="11px"
            fontWeight="600"
            px={2}
            py={1}
            borderRadius="3px"
          >
            {roleConfig.label}
          </Badge>
        )
      },
      meta: {
        width: '150px',
      },
    }),
    columnHelper.accessor('is_active', {
      header: 'Status',
      enableSorting: false,
      cell: (info) => {
        const isActive = info.getValue()
        return (
          <Badge
            bg={isActive ? '#E3FCEF' : '#FFEBE6'}
            color={isActive ? '#006644' : '#BF2600'}
            fontSize="11px"
            fontWeight="600"
            px={2}
            py={1}
            borderRadius="3px"
          >
            {isActive ? 'ACTIVE' : 'INACTIVE'}
          </Badge>
        )
      },
      meta: {
        width: '100px',
      },
    }),
    columnHelper.accessor('last_login', {
      header: 'Last Login',
      cell: (info) => {
        const lastLogin = info.getValue()
        if (!lastLogin) {
          return (
            <Text fontSize="12px" color="#5E6C84" fontStyle="italic">
              Never
            </Text>
          )
        }
        try {
          return (
            <Text fontSize="12px" color="#172B4D">
              {format(new Date(lastLogin), 'dd MMM yyyy HH:mm')}
            </Text>
          )
        } catch (e) {
          return <Text fontSize="12px" color="#5E6C84">—</Text>
        }
      },
      meta: {
        width: '160px',
      },
    }),
    columnHelper.accessor('date_joined', {
      header: 'Joined',
      cell: (info) => {
        const dateJoined = info.getValue()
        try {
          return (
            <Text fontSize="12px" color="#172B4D">
              {format(new Date(dateJoined), 'dd MMM yyyy')}
            </Text>
          )
        } catch (e) {
          return <Text fontSize="12px" color="#5E6C84">—</Text>
        }
      },
      meta: {
        width: '120px',
      },
    }),
  ]

  return (
    <Table
      data={data}
      columns={columns}
      isLoading={isLoading}
      enableSorting
      enablePagination
      enableRowSelection
      manualPagination
      manualSorting
      pageCount={pageCount}
      totalCount={totalCount}
      getRowId={(row) => row.id.toString()}
      state={{
        rowSelection,
        pagination,
        sorting,
      }}
      onRowSelectionChange={setRowSelection}
      onPaginationChange={onPaginationChange}
      onSortingChange={onSortingChange}
    />
  )
}

export default UsersTable
